import http, { getCommParams } from './request'
import { isInApp, toParam, isVersionBigger } from '@/utils/mUtil'
import store from '@/views/v2/store'
import Utils from '../utils/utils'
import { getDeviceInfo } from '@/utils/service'

const CASH_HOST = 'https://common-activity.bz.mgtv.com'
const CREDITS_HOST = 'https://credits.bz.mgtv.com'

window.CASH_PARAMS = {
  appId: '', // 海豚appId
  passcode: '', // 海豚passcode
  serviceId: '', // 海豚业务iD
}

// 缓存当前进行中的请求
let pendingRiskParamsPromise = null;

/**
 * 获取海豚信息
 * @param {number} retryCount - 当前重试次数
 * @returns {Promise}
 */
export function getRiskMngmtInfo(retryCount = 0) {
  return new Promise((resolve, reject) => {
    if (retryCount >= 6) {
      reject(new Error('Max retry count reached'));
      return;
    }

    if (isInApp()) {
      try {
        if (isVersionBigger(store.state.devicesInfo.appVersion)) {
          // 创建一个超时 Promise
          const timeoutPromise = new Promise((_, timeoutReject) => {
            setTimeout(() => {
              timeoutReject(new Error('Request timeout'));
            }, 500);
          });

          // 创建实际的 API 调用 Promise
          const apiPromise = new Promise((apiResolve) => {
            MgtvApi.callhandler(
              'getRiskMngmtInfo',
              {},
              (res) => {
                console.log('获取海豚参数成功：', res);
                apiResolve(res);
              }
            );
          });

          // 使用 Promise.race 来实现超时控制
          Promise.race([apiPromise, timeoutPromise])
            .then(resolve)
            .catch(() => {
              console.log(`第 ${retryCount + 1} 次请求超时，准备重试`);
              // 超时后递归重试
              getRiskMngmtInfo(retryCount + 1)
                .then(resolve)
                .catch(reject);
            });
        } else {
          resolve({});
        }
      } catch (e) {
        reject(e);
      }
    } else {
      resolve({});
    }
  });
}

/**
 * 提现海豚参数
 * @returns
 */
export async function getRiskParams(STR) {
  // 不支持海豚风控的端
   if(store.state.isNoneCashVer || await Utils.isSpecialVersion()){
      return {}
   }
    // 如果已经有请求在进行中，直接返回该请求的 Promise
    if (pendingRiskParamsPromise) {
        return pendingRiskParamsPromise;
    }

    // 创建新的请求
    pendingRiskParamsPromise = (async () => {
        try {
            let CASH_PARAMS = {};
            let res = await getRiskMngmtInfo();

            if (typeof(res) == 'string') {  // 兼容安卓返回的是object字符串
                res = JSON.parse(res);
            }

            if (res.code == 200) {
                CASH_PARAMS = typeof(res.data) == 'string' ? JSON.parse(res.data) : res.data;
            }

            return CASH_PARAMS;
        } catch (error) {
            console.error('getRiskParams error:', error);
            throw error;
        } finally {
            // 请求完成后清除缓存的 Promise
            pendingRiskParamsPromise = null;
        }
    })();

    return pendingRiskParamsPromise;
}

/**
 * 提现信息接口
 * @returns
 */
export async function getCashInfo() {
  const cash_params = await getRiskParams('getCashInfo')
  return http({
    method: 'get',
    url: `${CASH_HOST}/online/cashOut/task/detail`,
    params: {
      ...getCommParams(),
      ...cash_params,
      activityId: 52,
      saasId: 10,
    },
  })
}

// 提现用户信息
export async function getCashUserInfo() {
  const cash_params = await getRiskParams('getCashUserInfo')
  return http({
    method: 'get',
    url: `${CASH_HOST}/online/cashOut/userInfo`,
    params: {
      ...getCommParams(),
      ...cash_params,
    },
  })
}

// 微信授权认证
export async function wxAuth(code) {
  const cash_params = await getRiskParams()
  const urlParams = {
    ...getCommParams(),
    ...cash_params,
  }
  const paramStr = toParam(urlParams)
  return http({
    method: 'post',
    url: `${CASH_HOST}/online/cashOut/wx/auth?${paramStr}`,
    data: {
      authCode: code,
    },
  })
}

// 提现
export async function getCash(params) {
  const cash_params = await getRiskParams()
  const urlParams = {
    ...getCommParams(),
    ...cash_params,
  }
  const paramStr = toParam(urlParams)
  return http({
    method: 'post',
    url: `${CASH_HOST}/online/cashOut/applyFor?${paramStr}`,
    data: {
      ...params,
    },
  })
}

/**
 * 根据时间及收支类型查询用户现金、积分明细
 */
export async function getListByType(params) {
  const cash_params = await getRiskParams('getListByType')
  return http({
    method: 'get',
    url: `${CREDITS_HOST}/user/creditsLogByType`,
    params: {
      ...getCommParams(),
      ...cash_params,
      ...params,
    },
  })
}

// 获取公告栏信息
export async function getNoticeBoard(params) {
  return http({
    method: 'get',
    url: `${CASH_HOST}/online/activity/credit_center/notice_board`,
    params: {
      ...getCommParams(),
      ...params,
    },
  })
}

/**
 * @description: 获取全量任务
 * CREDIT_LIST_KEYS：；拉取签到任务和邀约任务userSettingStatus的值 1表示已签到，2表示未签到
 * CREDIT_CASH_KEYS：拉取现金类任务（第三方app换量）
 * CREDIT_POINT_KEYS：拉取积分类任务
 * @param {*} params
 * @return {*}
 */
export async function getActivityList(params) {
  const cash_params = await getRiskParams('getActivityList')
  const info = await getDeviceInfo();

  return http({
    method: 'get',
    url: `${CASH_HOST}/online/activity/list/by/saas-id`,
    params: {
      ...getCommParams(),
      ...cash_params,
      ...{
        saasId: 10,
        activityId: 33,
        mf: info.mf,
        ...params,
      },
    },
  })
}

/**
 * @description: 签到
 * @param {*} params taskSign 任务标识
 * @return {*}
 */
export async function checkIn(params) {
  const cash_params = await getRiskParams('checkIn')
  return http({
    method: 'post',
    url: `${CASH_HOST}/online/task/checkIn`,
    params: {
      ...getCommParams(),
      ...cash_params,
      ...{
        saasId: 10,
        ...params,
      },
    },
  })
}

/**
 * @description: 提醒通知（签到）
 * @param {*} params taskSign 任务标识
 * operation 1-订阅（默认）2-取消订阅
 * @return {*}
 */
export async function taskSubscribe(params) {
  return http({
    method: 'post',
    url: `${CASH_HOST}/online/task/checkIn/subscribe`,
    params: {
      ...getCommParams(),
      ...{
        saasId: 10,
        ...params,
      },
    },
  })
}

/**
 * @description: 发起邀请
 * @param {*} params taskId 任务ID
 * @return {*}
 */
export async function genInviteCode(params) {
  const cash_params = await getRiskParams()
  return http({
    method: 'get',
    url: `${CASH_HOST}/online/invite/gen/code`,
    params: {
      ...getCommParams(),
      ...cash_params,
      ...{
        saasId: 10,
        activityId: 33,
        ...params,
      },
    },
  })
}

/**
 * @description: 接受邀请
 * @param {*} params inviteCode 邀请码
 * @return {*}
 */
export async function acceptInvite(params) {
  const cash_params = await getRiskParams()
  return http({
    method: 'post',
    url: `${CASH_HOST}/online/invite/accept`,
    params: {
      ...getCommParams(),
      ...cash_params,
      ...{
        saasId: 10,
        ...params,
      },
    },
  })
}

/**
 * 积分年度清零展示信息
 */
export async function yearClear() {
  const cash_params = await getRiskParams()
  return http({
    method: 'get',
    url: `${CREDITS_HOST}/user/yearClear`,
    params: {
      ...getCommParams(),
      ...cash_params,
    },
  })
}

/**
 * @description: 根据邀请码获取用户信息
 * @param {*} params inviteCode 邀请码
 * @return {*}
 */
export async function getInfoByInviteCode(params) {
  return http({
    method: 'get',
    url: `${CASH_HOST}/online/invite/code/info`,
    params: {
      ...getCommParams(),
      ...{
        ...params,
      },
    },
  })
}

/**
 * @description: 浏览性任务接口 // 当上报接口使用
 * @param {*} params taskSign
 * @return {*}
 */
export async function eventJoin(params) {
  return http({
    method: 'post',
    url: `${CASH_HOST}/online/task/trigger/event/join`,
    params: {
      ...getCommParams(),
      ...{
        ...params,
      },
    },
  })
}



/**
 * @description: 现金任务 - 完成后领取接口
 * @param {*} params profitId、saasId、activityId、taskId
 * @return {*}
 */
export async function receiveTaskCash(params) {
  const cash_params = await getRiskParams()
  return http({
    method: 'post',
    url: `${CASH_HOST}/online/user/profit/receive-new`,
    params: {
      ...getCommParams(),
      ...cash_params,
      ...{
        saasId: 10,
        activityId: 33,
        ...params,
      },
    },
  })
}


/**
 * @description: 好友助力给用户带来的奖励 (视频观影奖励)
 * @param {*} params taskId
 * @return {*}
 */
export async function inviteGrossIncome(params) {
  const cash_params = await getRiskParams()
  return http({
    method: 'get',
    url: `${CASH_HOST}/online/invite/gross_income`,
    params: {
      ...getCommParams(),
      ...cash_params,
      ...{
        // saasId: 10,
        // activityId: 33,
        ...params,
      },
    },
  })
}

/**
 * @description: 我的最近邀请人（7天内有效）
 * @param {*} params taskId
 * @return {*}
 */
export async function recentInviters(params) {
  const cash_params = await getRiskParams()
  return http({
    method: 'get',
    url: `${CASH_HOST}/online/invite/recent/inviter`,
    params: {
      ...getCommParams(),
      ...cash_params,
      ...{
        // saasId: 10,
        // activityId: 33,
        ...params,
      },
    },
  })
}

/**
 * @description: 邀约提醒
 * @param {*} params taskId
 * @param {*} params subscribe 1-订阅（默认）2-取消订阅
 * @param {*} params taskSign 任务标识，前序接口获取
 * @return {*}
 */
export async function inviteSubscribe(params) {
  const cash_params = await getRiskParams()
  return http({
    method: 'post',
    url: `${CASH_HOST}/online/task/invite/subscribe`,
    params: {
      ...getCommParams(),
      ...cash_params,
      ...{
        saasId: 10,
        // activityId: 33,
        ...params,
      },
    },
  })
}